import React from 'react'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'

const fieldLabels = {
  bultosPalet: 'Bultos palet',
  calibreAmpliado: 'Calibre ampliado',
  cantidadPales: 'Cantidad palets',
  codAlmacen: 'Almacen',
  codCalibre: 'Cod. calibre',
  codCategoria: 'Cod. categoria',
  codCliente: 'Cod. cliente',
  codConfeccion: 'Cod. confeccion',
  codDirEnvio: 'Cod. destino',
  codEnvase: 'Cod. envase',
  codGrupoVariedad: 'Grupo variedad',
  codMarca: 'Cod. marca',
  codOrigen: 'Cod. origen',
  codPalet: 'Cod. palet',
  codTransportista: 'Cod. transportista',
  codVariedad: 'Cod. variedad',
  desCalibre: 'Calibre',
  desCategoria: 'Categoria',
  desCliente: 'Cliente',
  desConfeccion: 'Confeccion',
  desDirEnvio: 'Destino',
  desEnvase: 'Envase',
  desMarca: 'Marca',
  desOrigen: 'Origen',
  desPalet: 'Palet',
  desTransportista: 'Transportista',
  desVariedad: 'Variedad',
  eliminadoBusinessCentral: 'Eliminado Business Central',
  fechaCarga: 'Fecha carga',
  fechaCreacionBusinessCentral: 'Fecha creacion Business Central',
  fechaEliminadoBusinessCentral: 'Fecha eliminado Business Central',
  fechaEntrada: 'Fecha entrada',
  fechaFinalizacion: 'Fecha finalizacion',
  fechaModificacionBusinessCentral: 'Fecha modificacion Business Central',
  finalizado: 'Finalizado',
  ggn: 'GGN',
  kilosBrutos: 'Kilos brutos',
  kilosNetos: 'Kilos netos',
  kilosOficiales: 'Kilos oficiales',
  lote: 'Lote',
  matricula: 'Matricula',
  matricula2: 'Matricula 2',
  numDocExt: 'Num. doc. externo',
  numLinea: 'Num. linea',
  numLote: 'Num. lote',
  numPedido: 'Num. pedido',
  numRefPedido: 'Num. referencia pedido',
  observacion: 'Observacion',
  observacionesCarga: 'Observaciones carga',
  observacionesPedido: 'Observaciones pedido',
  sp: 'SP',
}

const formatFieldLabel = (key) => (
  fieldLabels[key]
  || String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (firstLetter) => firstLetter.toUpperCase())
)

const formatCambioValue = (value, formatDateTime) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'boolean') return value ? 'Si' : 'No'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value)
  if (typeof value === 'object') return JSON.stringify(value, null, 2)

  return String(value)
}

const parseJsonIfNeeded = (value) => {
  if (typeof value !== 'string') return value

  const trimmedValue = value.trim()

  if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) return value

  try {
    return JSON.parse(trimmedValue)
  } catch (error) {
    return value
  }
}

const getCambioRows = (cambio, formatDateTime) => (
  Object.entries(parseJsonIfNeeded(cambio) || {}).map(([key, value]) => ({
    key,
    label: formatFieldLabel(key),
    anterior: formatCambioValue(value?.anterior, formatDateTime),
    nuevo: formatCambioValue(value?.nuevo, formatDateTime),
  }))
)

const hasCambios = (cambio) => {
  const parsedCambio = parseJsonIfNeeded(cambio)

  return Boolean(parsedCambio && typeof parsedCambio === 'object' && Object.keys(parsedCambio).length > 0)
}

const getCambioFromEvento = (evento) => evento?.cambio || evento?.cambios

const getEventosBySeccion = (eventos, seccionFallback = 'Pedido') => {
  if (Array.isArray(eventos)) {
    return eventos.map((evento) => ({
      ...evento,
      seccion: evento.seccion || evento.tipo || seccionFallback,
    }))
  }

  if (!eventos || typeof eventos !== 'object') return []

  return Object.entries(eventos).flatMap(([seccion, eventosSeccion]) => {
    if (!Array.isArray(eventosSeccion)) return []

    const seccionLabel = seccion === 'cabecera'
      ? 'Cabecera'
      : seccion === 'linea'
        ? 'Linea'
        : formatFieldLabel(seccion)

    return eventosSeccion.map((evento) => ({
      ...evento,
      seccion: evento.seccion || seccionLabel,
    }))
  })
}

const getSeccionFromPath = (path) => {
  if (path.some((part) => String(part).toLowerCase().includes('cabecera'))) return 'Cabecera'
  if (path.some((part) => String(part).toLowerCase().includes('linea'))) return 'Linea'
  if (path.some((part) => String(part).toLowerCase().includes('planificacion'))) return 'Planificacion'

  return 'Pedido'
}

const getCambiosRecursivos = (source, formatDateTime) => {
  const cambios = []
  const cambiosVistos = new Set()
  const objetosVisitados = new Set()

  const visitar = (value, path = [], parent = null) => {
    const parsedValue = parseJsonIfNeeded(value)

    if (!parsedValue || typeof parsedValue !== 'object') return
    if (objetosVisitados.has(parsedValue)) return

    objetosVisitados.add(parsedValue)

    Object.entries(parsedValue).forEach(([key, child]) => {
      const parsedChild = parseJsonIfNeeded(child)
      const keyLower = key.toLowerCase()

      if ((keyLower === 'cambio' || keyLower === 'cambios') && hasCambios(parsedChild) && !cambiosVistos.has(parsedChild)) {
        cambiosVistos.add(parsedChild)
        cambios.push({
          key: `recursivo-${path.concat(key).join('-')}-${cambios.length}`,
          seccion: parent?.seccion || getSeccionFromPath(path),
          tipoEvento: parent?.tipoEvento || 'ACTUALIZADO',
          fechaEvento: parent?.fechaEvento ? formatDateTime(parent.fechaEvento) : '-',
          rows: getCambioRows(parsedChild, formatDateTime),
        })
      }

      visitar(parsedChild, path.concat(key), parsedValue)
    })
  }

  visitar(source)

  return cambios
}

const deduplicarCambios = (eventos) => {
  const vistos = new Set()

  return eventos.filter((evento) => {
    const firma = JSON.stringify({
      seccion: evento.seccion,
      tipoEvento: evento.tipoEvento,
      fechaEvento: evento.fechaEvento,
      rows: evento.rows,
    })

    if (vistos.has(firma)) return false

    vistos.add(firma)
    return true
  })
}

const getEventosWithCambios = (detallePedido, formatDateTime) => {
  const data = detallePedido?.data || {}
  const pedidoRaw = data.pedidoRaw || {}
  const pedido = data.pedido || pedidoRaw.pedido || data.planificacionRaw?.pedido || pedidoRaw || {}
  const posiblesEventos = [
    pedido.eventos,
    pedidoRaw.eventos,
    pedidoRaw.pedido?.eventos,
    data.eventos,
    data.lineaPedido?.eventos,
    data.cabeceraPedido?.eventos,
    data.planificacionRaw?.eventos,
    data.planificacionRaw?.pedido?.eventos,
  ]
  const directChanges = [
    data.cambio,
    data.cambios,
    pedidoRaw.cambio,
    pedidoRaw.cambios,
    pedidoRaw.pedido?.cambio,
    pedidoRaw.pedido?.cambios,
    pedidoRaw.linea?.cambio,
    pedidoRaw.cabecera?.cambio,
    data.lineaPedido?.cambio,
    data.lineaPedido?.cambios,
    data.cabeceraPedido?.cambio,
    data.cabeceraPedido?.cambios,
    data.planificacionRaw?.cambio,
    data.planificacionRaw?.cambios,
    data.planificacionRaw?.pedido?.cambio,
    data.planificacionRaw?.pedido?.cambios,
    data.planificacionRaw?.pedido?.linea?.cambio,
    data.planificacionRaw?.pedido?.cabecera?.cambio,
    pedido.cambio,
    pedido.cambios,
    pedido.linea?.cambio,
    pedido.linea?.cambios,
    pedido.cabecera?.cambio,
    pedido.cabecera?.cambios,
  ].filter(hasCambios)

  const eventosNormalizados = posiblesEventos
    .flatMap((eventos) => getEventosBySeccion(eventos))
    .filter((evento) => hasCambios(getCambioFromEvento(evento)))
    .map((evento, index) => ({
      key: evento.idEvento ?? `${evento.seccion}-${evento.fechaEvento ?? index}`,
      seccion: evento.seccion,
      tipoEvento: evento.tipoEvento || 'ACTUALIZADO',
      fechaEvento: evento.fechaEvento ? formatDateTime(evento.fechaEvento) : '-',
      rows: getCambioRows(getCambioFromEvento(evento), formatDateTime),
    }))

  const cambiosDirectos = directChanges.map((cambio, index) => ({
    key: `directo-${index}`,
    seccion: 'Pedido',
    tipoEvento: 'ACTUALIZADO',
    fechaEvento: '-',
    rows: getCambioRows(cambio, formatDateTime),
  }))

  return deduplicarCambios([
    ...eventosNormalizados,
    ...cambiosDirectos,
    ...getCambiosRecursivos(data, formatDateTime),
  ])
}

export const PedidoDetalleDialog = ({
  detallePedido,
  setDetallePedido,
  detallePedidoTitulo,
  detallePedidoBlocks,
  detallePedidoCambios = [],
  formatDateTime,
  getFechaInicioPalet,
  getFechaFinPalet,
}) => {
  const eventosCambios = detallePedidoCambios.length
    ? detallePedidoCambios
    : getEventosWithCambios(detallePedido, formatDateTime)

  return (
    <Dialog
      open={Boolean(detallePedido)}
      onClose={() => setDetallePedido(null)}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          width: 'min(1180px, calc(100vw - 32px))',
          height: 'min(88vh, calc(100vh - 32px))',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        Detalle del pedido {detallePedidoTitulo}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: '1 1 auto',
          maxHeight: 'calc(100vh - 150px)',
          minHeight: 0,
          overflowY: 'auto !important',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          backgroundColor: '#F8FAFC',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 1.5,
            alignItems: 'start',
            flexShrink: 0,
          }}
        >
          {detallePedidoBlocks.map((block) => (
            <Box
              key={block.title}
              sx={{
                border: '1px solid #DEE6F2',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 1.25, py: 0.8, borderBottom: '1px solid #E6ECF5', backgroundColor: '#FFFFFF' }}>
                <Typography sx={{ color: '#172033', fontWeight: 900, fontSize: '0.9rem' }}>
                  {block.title}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                {block.rows.map((row) => (
                  <Box
                    key={`${block.title}-${row.key}`}
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      minWidth: 0,
                      borderRight: { sm: '1px solid #F1F5F9' },
                      borderBottom: '1px solid #F1F5F9',
                    }}
                  >
                    <Typography sx={{ color: '#64748B', fontSize: '0.66rem', fontWeight: 900, textTransform: 'uppercase', lineHeight: 1.15 }}>
                      {row.label}
                    </Typography>
                    <Typography sx={{ color: '#172033', fontSize: '0.84rem', fontWeight: 700, overflowWrap: 'anywhere', mt: 0.25 }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {detallePedido?.tipo === 'planificacion' && Array.isArray(detallePedido.data?.palets) && detallePedido.data.palets.length > 0 && (
          <Box
            sx={{
              border: '1px solid #DEE6F2',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box sx={{ px: 1.25, py: 0.8, borderBottom: '1px solid #E6ECF5', backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: '#172033', fontWeight: 900, fontSize: '0.86rem' }}>
                Palets
              </Typography>

              <Chip size="small" label={`${detallePedido.data.palets.length} palets`} sx={{ fontWeight: 800 }} />
            </Box>

            <Box
              sx={{
                maxHeight: { xs: 190, md: 230 },
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {detallePedido.data.palets.map((palet) => (
                <Box
                  key={palet.idPlanificacionPalet ?? palet.numeroPalet}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderBottom: '1px solid #F1F5F9',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '90px repeat(3, minmax(0, 1fr))' },
                    gap: 0.75,
                  }}
                >
                  <Typography sx={{ color: '#172033', fontWeight: 900, fontSize: '0.8rem' }}>
                    Palet {palet.numeroPalet ?? '-'}
                  </Typography>
                  <Typography sx={{ color: '#475569', fontSize: '0.76rem', fontWeight: 700 }}>
                    Inicio: {formatDateTime(getFechaInicioPalet(palet))}
                  </Typography>
                  <Typography sx={{ color: '#475569', fontSize: '0.76rem', fontWeight: 700 }}>
                    Fin: {formatDateTime(getFechaFinPalet(palet))}
                  </Typography>
                  <Typography sx={{ color: '#475569', fontSize: '0.76rem', fontWeight: 700 }}>
                    Estado: {palet.estado ?? '-'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {eventosCambios.length > 0 && (
          <Box
            sx={{
              border: '1px solid #DEE6F2',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box sx={{ px: 1.25, py: 0.8, borderBottom: '1px solid #E6ECF5', backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: '#172033', fontWeight: 900, fontSize: '0.86rem' }}>
                Cambios realizados en el pedido
              </Typography>

              <Chip size="small" label={`${eventosCambios.length} eventos`} sx={{ fontWeight: 800 }} />
            </Box>

            <Box sx={{ display: 'grid', gap: 1, p: 1 }}>
              {eventosCambios.map((evento) => (
                <Box
                  key={evento.key}
                  sx={{
                    border: '1px solid #EEF2F7',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Box sx={{ px: 1, py: 0.7, backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip size="small" label={evento.seccion} color="primary" variant="outlined" sx={{ fontWeight: 800 }} />
                      <Typography sx={{ color: '#172033', fontWeight: 900, fontSize: '0.8rem' }}>
                        {evento.tipoEvento}
                      </Typography>
                    </Box>

                    <Typography sx={{ color: '#64748B', fontSize: '0.74rem', fontWeight: 800 }}>
                      {evento.fechaEvento}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '180px repeat(2, minmax(0, 1fr))' } }}>
                    {evento.rows.map((row) => (
                      <React.Fragment key={`${evento.key}-${row.key}`}>
                        <Box sx={{ px: 1, py: 0.75, borderBottom: '1px solid #F1F5F9', backgroundColor: '#FFFFFF' }}>
                          <Typography sx={{ color: '#64748B', fontSize: '0.68rem', fontWeight: 900, textTransform: 'uppercase' }}>
                            {row.label}
                          </Typography>
                        </Box>

                        <Box sx={{ px: 1, py: 0.75, borderBottom: '1px solid #F1F5F9', borderLeft: { md: '1px solid #F1F5F9' } }}>
                          <Typography sx={{ color: '#94A3B8', fontSize: '0.64rem', fontWeight: 900, textTransform: 'uppercase' }}>
                            Anterior
                          </Typography>
                          <Typography sx={{ color: '#475569', fontSize: '0.8rem', fontWeight: 700, overflowWrap: 'anywhere', mt: 0.2 }}>
                            {row.anterior}
                          </Typography>
                        </Box>

                        <Box sx={{ px: 1, py: 0.75, borderBottom: '1px solid #F1F5F9', borderLeft: { md: '1px solid #F1F5F9' }, backgroundColor: '#FFFBF5' }}>
                          <Typography sx={{ color: '#C97706', fontSize: '0.64rem', fontWeight: 900, textTransform: 'uppercase' }}>
                            Nuevo
                          </Typography>
                          <Typography sx={{ color: '#172033', fontSize: '0.8rem', fontWeight: 800, overflowWrap: 'anywhere', mt: 0.2 }}>
                            {row.nuevo}
                          </Typography>
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={() => setDetallePedido(null)}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
